import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Card, Message } from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';
import { renderQuotaWithPrompt } from '../../helpers/render';
import useSmartBackV81 from '../../hooks/useSmartBackV81';

const normalizeAccessGroups = (groups) => {
  const normalized = [];
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    const value = typeof group === 'string' ? group.trim() : '';
    if (value && !normalized.includes(value)) normalized.push(value);
  });
  return normalized;
};

const EditUserV2 = () => {
  const { t } = useTranslation();
  const params = useParams();
  const userId = params.id;
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({
    username: '',
    display_name: '',
    password: '',
    github_id: '',
    wechat_id: '',
    email: '',
    quota: 0,
    group: 'default',
    access_groups: ['default'],
  });
  const [groupOptions, setGroupOptions] = useState([]);
  const returnToUsers = useSmartBackV81('/user');
  const returnToSettings = useSmartBackV81('/setting');

  const handleInputChange = (_, { name, value }) => {
    setInputs((current) => ({ ...current, [name]: value }));
  };

  const handlePrimaryGroupChange = (_, { value }) => {
    setInputs((current) => ({ ...current, group: value }));
  };

  const handleAccessGroupsChange = (_, { value }) => {
    setInputs((current) => ({
      ...current,
      access_groups: normalizeAccessGroups(value),
    }));
  };

  const fetchGroups = useCallback(async () => {
    try {
      const response = await API.get('/api/group/');
      const groups = response?.data?.data || [];
      setGroupOptions(
        groups.map((group) => ({ key: group, text: group, value: group }))
      );
    } catch (error) {
      showError(error?.message || t('user.edit.groups_load_failed'));
    }
  }, [t]);

  const loadUser = useCallback(async () => {
    try {
      const response = userId
        ? await API.get(`/api/user/${userId}`)
        : await API.get('/api/user/self');
      const { success, message, data } = response.data;
      if (!success) {
        showError(message);
        return;
      }
      data.password = '';
      const accessGroups = normalizeAccessGroups(data.access_groups);
      data.access_groups =
        accessGroups.length > 0 ? accessGroups : [data.group || 'default'];
      setInputs(data);
    } catch (error) {
      showError(error?.message || t('user.edit.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [t, userId]);

  useEffect(() => {
    loadUser();
    if (userId) fetchGroups();
  }, [fetchGroups, loadUser, userId]);

  const submit = async () => {
    let response;
    if (userId) {
      const accessGroups = normalizeAccessGroups(inputs.access_groups);
      if (accessGroups.length === 0) {
        showError(t('user.edit.access_groups_required'));
        return;
      }
      const data = {
        ...inputs,
        id: parseInt(userId, 10),
        access_groups: accessGroups,
      };
      if (typeof data.quota === 'string') data.quota = parseInt(data.quota, 10);
      response = await API.put('/api/user/', data);
    } else {
      response = await API.put('/api/user/self', inputs);
    }
    const { success, message } = response.data;
    if (success) {
      showSuccess(t('user.messages.update_success'));
      if (userId) returnToUsers();
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container management-container-v4'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('user.edit.title')}</Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Input
              label={t('user.edit.username')}
              name='username'
              placeholder={t('user.edit.username_placeholder')}
              onChange={handleInputChange}
              value={inputs.username || ''}
              autoComplete='new-password'
            />
            <Form.Input
              label={t('user.edit.password')}
              name='password'
              type='password'
              placeholder={t('user.edit.password_placeholder')}
              onChange={handleInputChange}
              value={inputs.password || ''}
              autoComplete='new-password'
            />
            <Form.Input
              label={t('user.edit.display_name')}
              name='display_name'
              placeholder={t('user.edit.display_name_placeholder')}
              onChange={handleInputChange}
              value={inputs.display_name || ''}
              autoComplete='new-password'
            />

            {userId && (
              <>
                <Form.Dropdown
                  label={t('user.edit.primary_group')}
                  placeholder={t('user.edit.primary_group_placeholder')}
                  name='group'
                  fluid
                  search
                  selection
                  onChange={handlePrimaryGroupChange}
                  value={inputs.group || 'default'}
                  options={groupOptions}
                />
                <Form.Dropdown
                  label={t('user.edit.access_groups')}
                  placeholder={t('user.edit.access_groups_placeholder')}
                  name='access_groups'
                  fluid
                  multiple
                  required
                  search
                  selection
                  onChange={handleAccessGroupsChange}
                  value={inputs.access_groups || []}
                  options={groupOptions}
                />
                <Message info size='small'>
                  {t('user.edit.access_groups_help')}
                </Message>
                <Form.Input
                  label={`${t('user.edit.quota')}${renderQuotaWithPrompt(
                    inputs.quota,
                    t
                  )}`}
                  name='quota'
                  placeholder={t('user.edit.quota_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.quota}
                  type='number'
                  autoComplete='new-password'
                />
              </>
            )}

            <Form.Input
              label={t('user.edit.github_id')}
              name='github_id'
              value={inputs.github_id || ''}
              autoComplete='new-password'
              placeholder={t('user.edit.github_id_placeholder')}
              readOnly
            />
            <Form.Input
              label={t('user.edit.wechat_id')}
              name='wechat_id'
              value={inputs.wechat_id || ''}
              autoComplete='new-password'
              placeholder={t('user.edit.wechat_id_placeholder')}
              readOnly
            />
            <Form.Input
              label={t('user.edit.email')}
              name='email'
              value={inputs.email || ''}
              autoComplete='new-password'
              placeholder={t('user.edit.email_placeholder')}
              readOnly
            />
            <Button onClick={userId ? returnToUsers : returnToSettings}>
              {t('user.edit.buttons.cancel')}
            </Button>
            <Button positive onClick={submit}>
              {t('user.edit.buttons.submit')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditUserV2;
